import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import { showError } from 'utils';
import { Loading } from '../../../components/loading/Loading';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import CustomLink from 'components/TableLink';
import { fetchAllInventoryItemsForRecipe, fetchInventoryItemsByIds, removeInventoryItemFromRecipe } from 'services';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import _ from 'lodash';

const RecipeInventoryItemTable = ({ recipeId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [recipeInventoryItems, setRecipeInventoryItems] = useState<any>({});

  const fetchRecipeInventoryItemsFn = async () => {
    setLoading(true);
    try {
      const response = await fetchAllInventoryItemsForRecipe(recipeId);
      const itemIds = response.map(item => item?.inventoryItem?.objectId);
      const inventoryItemsResponse = await fetchInventoryItemsByIds(itemIds);
      const inventoryItemByIds = _.keyBy(inventoryItemsResponse, 'objectId');
      const inventoryItemsWithNamesData = response.map(item => {
        return { ...item, inventoryItemName: inventoryItemByIds[item?.inventoryItem?.objectId]?.name };
      });
      setRecipeInventoryItems(inventoryItemsWithNamesData);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipeInventoryItemsFn();
  }, []);

  const deleteRecipeInventoryItemFn = async (row) => {
    try {
      await removeInventoryItemFromRecipe(recipeId, row?.inventoryItem?.objectId);
      await fetchRecipeInventoryItemsFn();
    } catch (e) {
      showError(e);
    }
  };

  const columns = useMemo(() => [
    {
      name: 'Inventory Item Id',
      selector: row => row?.inventoryItem?.objectId,
      sortable: false,
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row?.inventoryItem?.objectId}
          linkTo={`/inventory-items/${row.inventoryItem.objectId}`}
        />
      ),
    },
    {
      name: 'Inventory Item Name',
      selector: 'inventoryItemName',
      sortable: true,
      width: '300px',
      cell: row => (
        <div>{row?.inventoryItemName ?? '-'}</div>
      ),
    },
    {
      name: 'Recipe Quantity',
      selector: 'recipeQuantity',
      sortable: false,
    },
    {
      name: 'Produce Quantity',
      selector: 'produceQuantity',
      sortable: false,
    },
    {
      name: 'Cutting Type',
      selector: row => row?.cuttingType ?? '-',
      sortable: false,
    },
    {
      name: 'Main Ingredient',
      selector: row => row?.isMainIngredient ? 'True' : 'False',
      sortable: true,
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      // eslint-disable-next-line react/display-name
      cell: (row: Record<string, any>) => (
        <div className={'ml-3'}>
          <DeleteActionForTable
            onClick={() => deleteRecipeInventoryItemFn(row)}
          />
        </div>
      ),
    },
  ], []);

  return (
    <div>
      {loading && <Loading />}
      <div className={styles['main-container']}>
        <DataTable
          keyField="objectId"
          columns={columns}
          noHeader={true}
          defaultSortField="isMainIngredient"
          data={recipeInventoryItems}
          highlightOnHover={true}
          customStyles={tableStyles}
          pagination={false}
        />
      </div>
    </div>
  );
};

export default RecipeInventoryItemTable;